import { Token, Route, Trade, TradeType, CurrencyAmount, TokenAmount, JSBI } from '@uniswap/sdk'
import { Contract } from '@ethersproject/contracts'

// 代币税费信息接口
export interface TokenTaxInfo {
  hasTax: boolean
  buyTax?: number
  sellTax?: number
  totalTax?: number
  isKnownTaxToken: boolean
  recommendedSlippage: number
  buyRecommendedSlippage?: number
  sellRecommendedSlippage?: number
}

// 已知的税费代币信息
const KNOWN_TAX_TOKENS: {
  [chainId: number]: {
    [address: string]: { buyTax: number; sellTax: number }
  }
} = {
  56: {
    '0x85416fb53603d30acb81bf66dc4282bd3170335b': { buyTax: 0, sellTax: 0 }, // BoTe
    '0xffff246519cda5f291fae3ffdeaaaaead0804588': { buyTax: 0, sellTax: 0 }, // WK
    '0xe18102869d32181aea317a40c5c4ce90ca591913': { buyTax: 100, sellTax: 10 }, // OG
    '0xb1939843fb9220719d13076dceefb0aeaef06666': { buyTax: 100, sellTax: 10 }, // BTCΞ
    '0xe277b2762a4355bdb04da7b98833c8aa7dff8888': { buyTax: 100, sellTax: 10 }, // NX
    '0xfc70b66f3b5f33b6f6b4239cf7c02cad6b131419': { buyTax: 100, sellTax: 10 }, // LTBS
    '0x424b7cd23ad589425492878bcd2613cf19274242': { buyTax: 100, sellTax: 10 } // 0x42
  },
  756: {
    '0x5bc748d53ffc0140eb0c1b5495643fa229f6c11e': { buyTax: 1, sellTax: 1 }, // BoTe
    '0xff888b5a5d8638864420083db97ba96bb716d22c': { buyTax: 10, sellTax: 46 }, // VT
    '0x6b2a54a13d7f5ca3262e4810824c97cbb7c74444': { buyTax: 0, sellTax: 5 } // KDOG
  }
}

// 从已知税费代币配置构建 TokenTaxInfo（同步，无需钱包）
export function getKnownTokenTaxInfo(chainId: number, address: string | undefined): TokenTaxInfo | null {
  const taxInfo = getKnownTaxTokenInfo(chainId, address)
  if (!taxInfo) return null

  const totalTax = Math.max(taxInfo.buyTax, taxInfo.sellTax)
  const buyRecommendedSlippage = taxInfo.buyTax > 0 ? taxInfo.buyTax + 0.5 : 0
  const sellRecommendedSlippage = taxInfo.sellTax > 0 ? taxInfo.sellTax + 0.5 : 0
  const recommendedSlippage = Math.max(buyRecommendedSlippage, sellRecommendedSlippage)

  return {
    hasTax: totalTax > 0,
    buyTax: taxInfo.buyTax,
    sellTax: taxInfo.sellTax,
    totalTax: totalTax,
    isKnownTaxToken: true,
    recommendedSlippage: recommendedSlippage,
    buyRecommendedSlippage: buyRecommendedSlippage,
    sellRecommendedSlippage: sellRecommendedSlippage
  }
}

// 检查代币是否为已知的税费代币
export function isKnownTaxToken(chainId: number, address: string | undefined): boolean {
  if (!chainId || !address) return false
  const normalizedAddress = address.toLowerCase()
  return KNOWN_TAX_TOKENS[chainId]?.[normalizedAddress] !== undefined || false
}

// 获取已知税费代币的详细信息
export function getKnownTaxTokenInfo(
  chainId: number,
  address: string | undefined
): { buyTax: number; sellTax: number } | null {
  if (!chainId || !address) return null
  const normalizedAddress = address.toLowerCase()
  return KNOWN_TAX_TOKENS[chainId]?.[normalizedAddress] || null
}

// 检查代币是否为 VT（不支持购买）
export function isVTToken(chainId: number, address: string | undefined): boolean {
  if (!chainId || !address) return false
  const normalizedAddress = address.toLowerCase()
  // VT 代币地址：0xff888b5a5d8638864420083db97ba96bb716d22c
  return chainId === 756 && normalizedAddress === '0xff888b5a5d8638864420083db97ba96bb716d22c'
}

// 安全检测代币税费（不执行实际转账）
export async function detectTokenTaxSafely(token: Token, library: any, account: string): Promise<TokenTaxInfo> {
  const chainId = token.chainId
  const tokenAddress = token.address

  // 首先检查是否为已知的税费代币
  const knownTokenTaxInfo = getKnownTokenTaxInfo(chainId, tokenAddress)
  if (knownTokenTaxInfo) {
    return knownTokenTaxInfo
  }

  try {
    // 创建代币合约实例
    const tokenContract = new Contract(
      tokenAddress,
      ['function name() external view returns (string)', 'function symbol() external view returns (string)'],
      library.getSigner()
    )

    // 检查代币的基本信息
    const [name, symbol] = await Promise.all([
      tokenContract.name().catch(() => 'Unknown'),
      tokenContract.symbol().catch(() => 'Unknown')
    ])

    // 检查代币名称和符号是否包含税费相关关键词
    const taxKeywords = ['tax', 'fee', 'burn', 'reflection', 'reward', 'dividend']
    const nameLower = name.toLowerCase()
    const symbolLower = symbol.toLowerCase()

    const hasTaxKeywords = taxKeywords.some(keyword => nameLower.includes(keyword) || symbolLower.includes(keyword))

    if (hasTaxKeywords) {
      return {
        hasTax: true,
        isKnownTaxToken: false,
        recommendedSlippage: 0
      }
    }

    // 检查代币合约是否有特殊的税费逻辑
    // 这里可以添加更多的检测逻辑，比如检查合约代码中的特定函数

    return {
      hasTax: false,
      isKnownTaxToken: false,
      recommendedSlippage: 0
    }
  } catch (error) {
    console.debug('Error in safe tax detection:', error)
    return {
      hasTax: false,
      isKnownTaxToken: false,
      recommendedSlippage: 0
    }
  }
}

// 计算路由中的税费影响
export function calculateRouteTaxImpact(route: Route): number {
  let totalTax = 0

  // 遍历路径中的每个代币交易对
  for (let i = 0; i < route.path.length - 1; i++) {
    const currentToken = route.path[i]
    const nextToken = route.path[i + 1]

    // 当前代币被卖出
    const currentTaxInfo = getKnownTaxTokenInfo(currentToken.chainId, currentToken.address)
    if (currentTaxInfo && currentTaxInfo.sellTax > 0) {
      totalTax += currentTaxInfo.sellTax
    }

    // 下一个代币被买入
    const nextTaxInfo = getKnownTaxTokenInfo(nextToken.chainId, nextToken.address)
    if (nextTaxInfo && nextTaxInfo.buyTax > 0) {
      totalTax += nextTaxInfo.buyTax
    }
  }
  return totalTax
}

// 计算税费调整后的输出金额
export function getTaxAdjustedOutputAmount(trade: Trade): CurrencyAmount {
  const routeTaxImpact = calculateRouteTaxImpact(trade.route)

  if (routeTaxImpact > 0) {
    // 应用税费影响：实际可得 = 原始输出 * (1 - 税费百分比)
    const taxMultiplier = 1 - routeTaxImpact / 100

    // 安全检查：确保 taxMultiplier 不为负数
    if (taxMultiplier <= 0) {
      console.warn('Tax multiplier is negative or zero, returning original amount')
      return trade.outputAmount
    }

    const adjustedRaw = JSBI.divide(
      JSBI.multiply(trade.outputAmount.raw, JSBI.BigInt(Math.floor(taxMultiplier * 10000))),
      JSBI.BigInt(10000)
    )

    return trade.outputAmount instanceof TokenAmount
      ? new TokenAmount(trade.outputAmount.token, adjustedRaw)
      : CurrencyAmount.ether(adjustedRaw)
  }

  return trade.outputAmount
}

// 计算税费调整后的输入金额（对于 EXACT_OUTPUT 交易）
export function getTaxAdjustedInputAmount(trade: Trade): CurrencyAmount {
  if (trade.tradeType === TradeType.EXACT_OUTPUT) {
    const routeTaxImpact = calculateRouteTaxImpact(trade.route)

    if (routeTaxImpact > 0) {
      // 对于 EXACT_OUTPUT，需要考虑税费对输入的影响
      // 实际需要输入 = 原始输入 / (1 - 税费百分比)
      const taxMultiplier = 1 - routeTaxImpact / 100
      if (taxMultiplier > 0) {
        const adjustedRaw = JSBI.divide(
          JSBI.multiply(trade.inputAmount.raw, JSBI.BigInt(10000)),
          JSBI.BigInt(Math.floor(taxMultiplier * 10000))
        )

        return trade.inputAmount instanceof TokenAmount
          ? new TokenAmount(trade.inputAmount.token, adjustedRaw)
          : CurrencyAmount.ether(adjustedRaw)
      }
    }
  }

  return trade.inputAmount
}

// 获取滑点建议文本
export function getSlippageRecommendation(taxInfo: TokenTaxInfo, t: any, isInputToken: boolean = true): string {
  if (!taxInfo.hasTax) {
    return ''
  }

  // isInputToken: true 表示是输入代币（from），此时是卖出操作
  // isInputToken: false 表示是输出代币（to），此时是买入操作
  const isSellOperation = isInputToken
  const isBuyOperation = !isInputToken

  if (isSellOperation && taxInfo.sellTax && taxInfo.sellTax > 0) {
    // 卖出操作，显示卖出税费
    return t('taxDetectedSell', {
      sellTax: taxInfo.sellTax.toFixed(1),
      sellSlippage: taxInfo.sellRecommendedSlippage?.toFixed(1)
    })
  } else if (isBuyOperation && taxInfo.buyTax && taxInfo.buyTax > 0) {
    // 买入操作，显示买入税费
    return t('taxDetectedBuy', {
      buyTax: taxInfo.buyTax.toFixed(1),
      buySlippage: taxInfo.buyRecommendedSlippage?.toFixed(1)
    })
  }

  // 对于已知的税费代币或无法获取具体数值的情况，只显示通用提醒
  return t('taxDetectedGeneral')
}
